import React, { useEffect, useMemo, useState } from 'react';
import { Container, Row, Col, Card, CardBody, Button, Spinner, Input, Label } from 'reactstrap';
import { FaChartLine, FaBoxOpen, FaCalendarAlt, FaMoneyBillWave, FaFileExcel, FaToolbox } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { saleActions } from '../../sagas/saleSlice';
import * as XLSX from 'xlsx';
import Swal from 'sweetalert2';

const Dashboard = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const role = useSelector((state) => state.auth.role);
  const {
    totalVentes,
    valeurStock,
    lowStockItems,
    isLoading,
    totalDepenses,
    totalPaiements,
  } = useSelector(state => state.sales);

  // Helpers
  const pad = (n) => String(n).padStart(2, '0');
  const toISODate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

  // Defaults: today → today
  const today = useMemo(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate());
  }, []);
  const [startDate, setStartDate] = useState(toISODate(today));
  const [endDate, setEndDate] = useState(toISODate(today));

  // Load dashboard data whenever dates change
  useEffect(() => {
    if (!startDate || !endDate) return;
    if (new Date(startDate) > new Date(endDate)) return;
    dispatch(saleActions.getSalesData({ startDate, endDate }));
  }, [dispatch, startDate, endDate]);

  const validateDates = () => {
    if (!startDate || !endDate) {
      Swal.fire({ icon: 'warning', title: t('common.warning') || 'Attention', text: t('dashboard.selectDates') || 'Veuillez sélectionner une période.' });
      return false;
    }
    if (new Date(startDate) > new Date(endDate)) {
      Swal.fire({ icon: 'error', title: t('common.error') || 'Erreur', text: t('dashboard.invalidRange') || 'Date début supérieure à date fin.' });
      return false;
    }
    return true;
  };

  const handleRefresh = () => {
    if (!validateDates()) return;
    dispatch(saleActions.getSalesData({ startDate, endDate }));
  };

  const handleExportSalesReport = async () => {
    if (!validateDates()) return;

    try {
      Swal.fire({
        title: t('dashboard.exportLoading') || 'Export en cours…',
        text: t('dashboard.exportLoadingText') || 'Veuillez patienter.',
        allowOutsideClick: false,
      });
      Swal.showLoading();

      dispatch(saleActions.getSalesReport({
        startDate,
        endDate,
        onSuccess: (data) => {
          const wb = XLSX.utils.book_new();
          const ws = XLSX.utils.json_to_sheet(
            (data || []).map(sale => ({
              "Désignation": sale.productDescription,
              "Quantité": sale.quantity,
              "Total": sale.total
            }))
          );
          XLSX.utils.book_append_sheet(wb, ws, `Sales_${startDate}_to_${endDate}`);
          XLSX.writeFile(wb, `sales_report_${startDate}_to_${endDate}.xlsx`);

          Swal.fire({
            icon: 'success',
            title: t('dashboard.exportSuccess') || 'Export réussi',
            text: t('dashboard.exportSuccessText') || 'Votre fichier a été généré.',
            timer: 2000,
            showConfirmButton: false
          });
        }
      }));
    } catch (error) {
      console.error('Export error:', error);
      Swal.fire({
        icon: 'error',
        title: t('dashboard.exportError') || 'Erreur export',
        text: error.message,
        confirmButtonColor: '#3085d6'
      });
    }
  };

  return (
    <Container fluid>
      {/* Date pickers */}
      <Row className="mb-4">
        <Col>
          <Card className="shadow-sm">
            <CardBody className="d-flex flex-wrap align-items-end justify-content-between gap-3">
              <div className="d-flex align-items-center gap-2">
                <FaCalendarAlt className="text-primary" size={20} />
                <h4 className="mb-0">{t('dashboard.reports')}</h4>
              </div>

              <div className="d-flex flex-wrap align-items-end gap-3">
                <div className="d-flex align-items-center gap-2">
                  <Label className="fw-semibold mb-0" htmlFor="startDate">
                    {t('dashboard.startDate') || 'Date début'}:
                  </Label>
                  <Input
                    id="startDate"
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    max={endDate || undefined}
                    style={{ width: 180 }}
                  />
                </div>

                {/* Date fin */}
                <div className="d-flex align-items-center gap-2">
                  <Label className="fw-semibold mb-0" htmlFor="endDate">
                    {t('dashboard.endDate') || 'Date fin'}:
                  </Label>
                  <Input
                    id="endDate"
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    min={startDate || undefined}
                    style={{ width: 180 }}
                  />
                </div>

                <div className="d-flex align-items-end gap-2">
                  <Button color="primary" onClick={handleRefresh} disabled={isLoading}>
                    {isLoading ? <Spinner size="sm" className="me-1" /> : null}
                    {t('common.apply') || 'Appliquer'}
                  </Button>

                  {/* <Button color="success" onClick={handleExportSalesReport} disabled={isLoading}>
                    <FaFileExcel className="me-2" />
                    {isLoading ? <Spinner size="sm" className="me-1" /> : null}
                    {t('dashboard.exportSales') || 'Exporter ventes'}
                  </Button> */}
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Sales Summary Cards (Admin only) */}
      {role === 'ROLE_ADMIN' && (
        <Row className="mb-4">
          <Col md="3">
            <Card className="shadow-sm h-100">
              <CardBody>
                <div className="d-flex align-items-center mb-3">
                  <FaChartLine className="text-primary me-2" size={20} />
                  <h5 className="mb-0">{t('dashboard.totalVentes')}</h5>
                </div>
                <h3 className="mb-0">{totalVentes} DH</h3>
              </CardBody>
            </Card>
          </Col>
          <Col md="3">
            <Card className="shadow-sm h-100">
              <CardBody>
                <div className="d-flex align-items-center mb-3">
                  <FaMoneyBillWave className="text-success me-2" size={20} />
                  <h5 className="mb-0">{t('dashboard.totalPaiements')}</h5>
                </div>
                <h3 className="mb-0">{totalPaiements} DH</h3>
              </CardBody>
            </Card>
          </Col>
          <Col md="3">
            <Card className="shadow-sm h-100">
              <CardBody>
                <div className="d-flex align-items-center mb-3">
                  <FaToolbox className="text-success me-2" size={20} />
                  <h5 className="mb-0">{t('dashboard.totalDepenses')}</h5>
                </div>
                <h3 className="mb-0">{totalDepenses} DH</h3>
              </CardBody>
            </Card>
          </Col>
          <Col md="3">
            <Card className="shadow-sm h-100">
              <CardBody>
                <div className="d-flex align-items-center mb-3">
                  <FaMoneyBillWave className="text-success me-2" size={20} />
                  <h5 className="mb-0">{t('dashboard.valeurStock')}</h5>
                </div>
                <h3 className="mb-0">{valeurStock} DH</h3>
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}

      {/* Low Stock Alert */}
      <Row>
        <Col>
          <Card className="shadow-sm">
            <CardBody style={{ overflowY: 'auto', maxHeight: '450px' }}>
              <div className="d-flex align-items-center mb-3">
                <FaBoxOpen className="text-warning me-2" size={20} />
                <h5 className="mb-0">{t('dashboard.lowStockAlert')}</h5>
              </div>
              <div className="table-responsive">
                <table className="table table-sm table-hover">
                  <thead>
                    <tr>
                      <th>{t('product.code')}</th>
                      <th>{t('product.description')}</th>
                      <th>{t('product.stockQty')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lowStockItems.map(item => (
                      <tr key={item.id}>
                        <td>{item.code}</td>
                        <td>{item.name}</td>
                        <td className="text-danger fw-bold">{item.stock}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default Dashboard;
